import React, { useState, useEffect, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import useSWR from 'swr';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { 
  User, Download, Search, Mail, Phone, Calendar, Loader2, 
  ChevronLeft, ChevronRight, Eye, Check, DollarSign, TrendingUp,
  FileText, ShoppingCart, UserCheck, RefreshCw, ChevronDown, ChevronUp,
  Clock, CreditCard, Users, MapPin, Globe, X, Printer, Shield,
  AlertTriangle, Smartphone, Monitor, RotateCcw, Flame, Sparkles, Copy
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import { Badge } from './ui/badge';
import { BACKEND_URL } from '../config/api';
import RefundModal from './RefundModal';
import TabPageLayout, { PageLoader } from './TabPageLayout';
import ExportDropdown from './ui/ExportDropdown';
import { STATUS_COLORS, getStatusBadge as getStatusBadgeFromPalette, getAvatarBgColor, getAvatarTextColor } from '../config/statusColors';

// Sanitize search input - remove special chars that could cause issues
const sanitizeSearchInput = (input) => {
  if (!input) return '';
  // Remove potentially dangerous characters, keep alphanumeric, spaces, @, ., -
  return input
    .replace(/[<>{}[\]\\\/^~`|]/g, '') // Remove dangerous chars
    .trim()
    .slice(0, 100); // Max 100 chars
};

// Expandable Contact Detail Panel
const ContactDetailPanel = ({ contact, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [details, setDetails] = useState(null);
  const [activeTab, setActiveTab] = useState('timeline');
  const [generatingReport, setGeneratingReport] = useState(false);
  
  // Pagination state for each tab
  const [bookingsPage, setBookingsPage] = useState(1);
  const [timelinePage, setTimelinePage] = useState(1);
  const [waiversPage, setWaiversPage] = useState(1);
  const [connectionsPage, setConnectionsPage] = useState(1);
  const ITEMS_PER_PAGE = 5;
  
  // Refund modal state
  const [refundModalOpen, setRefundModalOpen] = useState(false);
  const [selectedBookingForRefund, setSelectedBookingForRefund] = useState(null);

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/relations/${encodeURIComponent(contact.id)}/details`);
        setDetails(response.data);
      } catch (error) {
        console.error('Error fetching details:', error);
        toast.error('Failed to load contact details');
      } finally {
        setLoading(false);
      }
    };
    fetchDetails();
  }, [contact.id]);

  const formatDate = (dateStr, timeStr = null) => {
    if (!dateStr) return '-';
    try {
      // Handle date-only strings (YYYY-MM-DD) to avoid timezone conversion issues
      // Parse as local date, not UTC
      if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        const [year, month, day] = dateStr.split('-').map(Number);
        const date = new Date(year, month - 1, day); // month is 0-indexed
        return date.toLocaleDateString('en-US', { 
          month: 'short', day: 'numeric', year: 'numeric',
          timeZoneName: 'short'
        });
      }
      // For full ISO timestamps, use regular parsing with timezone
      return new Date(dateStr).toLocaleDateString('en-US', { 
        month: 'short', day: 'numeric', year: 'numeric', 
        hour: '2-digit', minute: '2-digit',
        timeZoneName: 'short'
      });
    } catch { return '-'; }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleGenerateReport = async () => {
    try {
      setGeneratingReport(true);
      toast.info('Generating official report...');
      
      const token = localStorage.getItem('jwtToken');
      const url = `${BACKEND_URL}/api/relations/${encodeURIComponent(contact.id)}/chargeback-report`;
      
      // Use XMLHttpRequest instead of fetch to bypass rrweb interceptor
      const blob = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('GET', url, true);
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.responseType = 'blob';
        
        xhr.onload = function() {
          if (xhr.status === 200) {
            resolve(xhr.response);
          } else {
            reject(new Error(`Server returned ${xhr.status}: ${xhr.statusText}`));
          }
        };
        
        xhr.onerror = function() {
          reject(new Error('Network error occurred'));
        };
        
        xhr.ontimeout = function() {
          reject(new Error('Request timed out'));
        };
        
        xhr.timeout = 60000; // 60 second timeout for large reports
        xhr.send();
      });
      
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `official-report-${contact.name?.replace(/\s+/g, '-') || 'customer'}-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success('Report downloaded!');
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error(error.message || 'Failed to generate report');
    } finally {
      setGeneratingReport(false);
    }
  };

  if (loading) {
    return ReactDOM.createPortal(
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[9999] flex items-center justify-center">
        <div className="bg-white rounded-xl p-8">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto" />
          <p className="mt-4 text-gray-600">Loading contact details...</p>
        </div>
      </div>,
      document.body
    );
  }

  // Currency formatting helper
  const formatCurrency = (amount, currency = 'USD') => {
    const currencySymbols = {
      'USD': '$',
      'AED': 'AED ',
      'EUR': '€',
      'GBP': '£',
      'INR': '₹',
    };
    const symbol = currencySymbols[currency?.toUpperCase()] || currency + ' ';
    return `${symbol}${(amount || 0).toFixed(2)}`;
  };

  // Get primary currency from details
  const primaryCurrency = details?.summary?.currency || 'USD';

  // Helper function to paginate arrays
  const paginateData = (data, page, itemsPerPage) => {
    if (!data) return { items: [], totalPages: 0, currentPage: page };
    const totalPages = Math.ceil(data.length / itemsPerPage);
    const start = (page - 1) * itemsPerPage;
    const items = data.slice(start, start + itemsPerPage);
    return { items, totalPages, currentPage: page, total: data.length };
  };

  // Get paginated data for each tab
  const paginatedBookings = paginateData(details?.bookings, bookingsPage, ITEMS_PER_PAGE);
  const paginatedTimeline = paginateData(details?.timeline, timelinePage, ITEMS_PER_PAGE);
  const paginatedWaivers = paginateData(details?.waiverSignatures, waiversPage, ITEMS_PER_PAGE);
  const paginatedConnections = paginateData(details?.coParticipants, connectionsPage, ITEMS_PER_PAGE);

  // Pagination component
  const PaginationControls = ({ paginated, setPage }) => {
    if (paginated.totalPages <= 1) return null;
    return (
      <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
        <p className="text-xs text-gray-500">
          Showing {((paginated.currentPage - 1) * ITEMS_PER_PAGE) + 1}-{Math.min(paginated.currentPage * ITEMS_PER_PAGE, paginated.total)} of {paginated.total}
        </p>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={paginated.currentPage === 1}
            className="p-1.5 rounded border border-gray-200 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-xs text-gray-600 px-2">
            {paginated.currentPage} / {paginated.totalPages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(paginated.totalPages, p + 1))}
            disabled={paginated.currentPage === paginated.totalPages}
            className="p-1.5 rounded border border-gray-200 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  };

  // Payment status helper
  const getPaymentStatusLabel = (status) => {
    if (status === 'paid') return { label: 'Payment Succeeded', color: 'text-green-600', bgColor: 'bg-green-100' };
    if (status === 'payment_failed' || status === 'failed') return { label: 'Payment Failed', color: 'text-gray-500', bgColor: 'bg-gray-100' };
    if (status === 'pending') return { label: 'Payment Pending', color: 'text-amber-600', bgColor: 'bg-amber-100' };
    return { label: status || 'Unknown', color: 'text-gray-500', bgColor: 'bg-gray-100' };
  };

  const content = (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[9999] overflow-y-auto py-4 sm:py-8 px-4 sm:px-0">
      <div className="bg-white w-full max-w-full sm:max-w-4xl mx-auto shadow-2xl flex flex-col rounded-lg sm:rounded-xl overflow-hidden print:max-w-none print:max-h-none print:overflow-visible print:rounded-none">
        {/* Header - Mobile responsive */}
        <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white p-4 sm:p-6 flex-shrink-0 print:bg-gray-900">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-white/10 flex items-center justify-center text-xl sm:text-2xl font-bold flex-shrink-0">
                {(contact.name || 'U')[0].toUpperCase()}
              </div>
              <div className="min-w-0">
                <h2 className="text-lg sm:text-xl font-bold truncate">{contact.name}</h2>
                <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-1 sm:gap-3 mt-1 text-xs sm:text-sm text-gray-300">
                  {contact.email && <span className="flex items-center gap-1 truncate"><Mail className="w-3 h-3 flex-shrink-0" /><span className="truncate">{contact.email}</span></span>}
                  {contact.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3 flex-shrink-0" />{contact.phone}</span>}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 print:hidden flex-shrink-0">
              <Button 
                onClick={handleGenerateReport}
                disabled={generatingReport}
                className="bg-blue-600 hover:bg-blue-700 text-white text-xs sm:text-sm"
                size="sm"
              >
                {generatingReport ? (
                  <Loader2 className="w-4 h-4 mr-1 sm:mr-2 animate-spin" />
                ) : (
                  <FileText className="w-4 h-4 mr-1 sm:mr-2" />
                )}
                <span className="hidden sm:inline">Generate Report</span>
                <span className="sm:hidden">Report</span>
              </Button>
              <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
          
          {/* Summary Stats - Mobile responsive */}
          {details?.summary && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4 mt-4 sm:mt-6">
              <div className="bg-white/10 rounded-lg p-2 sm:p-3 text-center">
                <div className="text-lg sm:text-2xl font-bold">{formatCurrency(details.summary.totalSpent, primaryCurrency)}</div>
                <div className="text-[10px] sm:text-xs text-gray-300 mt-1">Total Spent</div>
              </div>
              <div className="bg-white/10 rounded-lg p-2 sm:p-3 text-center">
                <div className="text-lg sm:text-2xl font-bold">{details.summary.totalBookings || 0}</div>
                <div className="text-[10px] sm:text-xs text-gray-300 mt-1">Bookings</div>
              </div>
              <div className="bg-white/10 rounded-lg p-2 sm:p-3 text-center">
                <div className="text-lg sm:text-2xl font-bold">{details.summary.totalWaivers || 0}</div>
                <div className="text-[10px] sm:text-xs text-gray-300 mt-1">Waivers Signed</div>
              </div>
              <div className="bg-white/10 rounded-lg p-2 sm:p-3 text-center">
                <div className="text-lg sm:text-2xl font-bold">{details.summary.totalCoParticipants || 0}</div>
                <div className="text-[10px] sm:text-xs text-gray-300 mt-1">Connected People</div>
              </div>
            </div>
          )}
          
          {/* Identity Audit - Shows all name/email/phone variations */}
          {details?.identityAudit && (details.identityAudit.hasMultipleNames || details.identityAudit.hasMultipleEmails || details.identityAudit.hasMultiplePhones) && (
            <div className="mt-4 bg-amber-500/20 border border-amber-400/30 rounded-lg p-3">
              <h4 className="text-xs font-semibold text-amber-200 mb-2 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                Identity Variations Detected
              </h4>
              <div className="flex flex-wrap gap-2 text-xs">
                {details.identityAudit.hasMultipleNames && (
                  <div className="bg-white/10 rounded px-2 py-1">
                    <span className="text-gray-400">Names: </span>
                    <span className="text-white">{details.identityAudit.names.join(', ')}</span>
                  </div>
                )}
                {details.identityAudit.hasMultipleEmails && (
                  <div className="bg-white/10 rounded px-2 py-1">
                    <span className="text-gray-400">Emails: </span>
                    <span className="text-white">{details.identityAudit.emails.join(', ')}</span>
                  </div>
                )}
                {details.identityAudit.hasMultiplePhones && (
                  <div className="bg-white/10 rounded px-2 py-1">
                    <span className="text-gray-400">Phones: </span>
                    <span className="text-white">{details.identityAudit.phones.join(', ')}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Tabs - Mobile responsive with horizontal scroll */}
        <div className="border-b border-gray-200 px-3 sm:px-6 flex-shrink-0 print:hidden overflow-x-auto">
          <div className="flex gap-0 sm:gap-1 -mb-px min-w-max">
            {[
              { id: 'timeline', label: 'Timeline', icon: Clock },
              { id: 'bookings', label: 'Bookings', icon: Calendar },
              { id: 'waivers', label: 'Waivers', icon: Shield },
              { id: 'connections', label: 'Connections', icon: Users }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-3 text-xs sm:text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 print:overflow-visible">
          {/* Timeline Tab - Enhanced with payment status */}
          {activeTab === 'timeline' && (
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900 mb-4">Activity Timeline</h3>
              {details?.timeline?.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No activity recorded</p>
              ) : (
                <>
                  <div className="relative">
                    <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>
                    {paginatedTimeline.items?.map((event, idx) => {
                      const paymentInfo = event.type === 'booking' ? getPaymentStatusLabel(event.paymentStatus) : null;
                      
                      return (
                        <div key={idx} className="relative pl-10 pb-6 last:pb-0 flex items-start">
                          <div className={`absolute left-2 top-0 w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                            event.type === 'booking' ? (
                              event.paymentStatus === 'paid' ? 'bg-green-500' :
                              event.paymentStatus === 'payment_failed' || event.paymentStatus === 'failed' ? 'bg-gray-400' :
                              'bg-blue-500'
                            ) :
                            event.type === 'waiver' ? 'bg-purple-500' :
                            event.type === 'lead' ? 'bg-orange-500' : 'bg-gray-400'
                          }`}>
                            {event.type === 'booking' && <CreditCard className="w-3 h-3 text-white" />}
                            {event.type === 'waiver' && <FileText className="w-3 h-3 text-white" />}
                            {event.type === 'lead' && <TrendingUp className="w-3 h-3 text-white" />}
                          </div>
                          <div className="bg-gray-50 rounded-lg p-3 sm:p-4 flex-1">
                            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-gray-900 text-sm sm:text-base">{event.description}</p>
                                {event.type === 'booking' && event.amount > 0 && (
                                  <div className="flex flex-wrap items-center gap-2 mt-1">
                                    <span className={`text-sm font-semibold ${paymentInfo?.color || 'text-gray-600'}`}>
                                      {formatCurrency(event.amount, event.currency || primaryCurrency)}
                                    </span>
                                    {paymentInfo && (
                                      <span className={`text-xs px-2 py-0.5 rounded-full ${paymentInfo.bgColor} ${paymentInfo.color}`}>
                                        {paymentInfo.label}
                                      </span>
                                    )}
                                  </div>
                                )}
                                {event.ipAddress && (
                                  <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                                    <Globe className="w-3 h-3" />IP: {event.ipAddress}
                                  </p>
                                )}
                              </div>
                              <span className="text-xs text-gray-500 flex-shrink-0">{formatDate(event.date)}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <PaginationControls paginated={paginatedTimeline} setPage={setTimelinePage} />
                </>
              )}
            </div>
          )}

          {/* Bookings Tab - Mobile responsive with currency support */}
          {activeTab === 'bookings' && (
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900 mb-4">All Bookings ({details?.bookings?.length || 0})</h3>
              {details?.bookings?.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No bookings found</p>
              ) : (
                <>
                  <div className="space-y-3">
                    {paginatedBookings.items?.map((booking, idx) => {
                      const isPaid = booking.paymentStatus === 'paid';
                      const isFailed = booking.paymentStatus === 'payment_failed' || booking.paymentStatus === 'failed';
                      const paymentInfo = getPaymentStatusLabel(booking.paymentStatus);
                      
                      return (
                        <div key={idx} className="border border-gray-200 rounded-lg p-3 sm:p-4 hover:bg-gray-50">
                          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex flex-wrap items-center gap-2">
                                <h4 className="font-semibold text-gray-900 text-sm sm:text-base">{booking.serviceName}</h4>
                                {booking.isWaiverSigner && <span className="text-xs px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 border border-purple-200">Signed Waiver</span>}
                              </div>
                              <p className="text-xs sm:text-sm text-gray-600 mt-1">
                                {formatDate(booking.date)} {booking.time && `at ${booking.time}`}
                              </p>
                              <div className="flex flex-wrap items-center gap-2 sm:gap-4 mt-2 text-xs text-gray-500">
                                <span className="flex items-center gap-1"><Users className="w-3 h-3" />{booking.guests} guests</span>
                                <span className={`px-2 py-0.5 rounded-full border ${
                                  booking.status === 'confirmed' ? 'bg-green-50 text-green-700 border-green-200' :
                                  booking.status === 'cancelled' ? 'bg-red-50 text-red-700 border-red-200' :
                                  booking.status === 'abandoned' ? 'bg-orange-50 text-orange-700 border-orange-200' :
                                  'bg-gray-50 text-gray-700 border-gray-200'
                                }`}>{booking.status === 'abandoned' ? 'Cart Abandoned' : booking.status}</span>
                                <span className={`px-2 py-0.5 rounded-full border ${paymentInfo.bgColor} ${paymentInfo.color} ${
                                  paymentInfo.label === 'Payment Succeeded' ? 'border-green-200' :
                                  paymentInfo.label === 'refunded' ? 'border-orange-200' :
                                  paymentInfo.label === 'Payment Failed' ? 'border-gray-200' :
                                  'border-gray-200'
                                }`}>
                                  {paymentInfo.label}
                                </span>
                              </div>
                              {booking.stripePaymentIntentId && (
                                <p className="text-xs text-gray-400 mt-2 font-mono truncate">
                                  Payment ID: {booking.stripePaymentIntentId}
                                </p>
                              )}
                            </div>
                            <div className="text-left sm:text-right flex-shrink-0">
                              <p className={`text-lg font-bold ${paymentInfo.color}`}>
                                {formatCurrency(booking.amount, booking.currency || primaryCurrency)}
                              </p>
                              {/* Multi-Currency Indicator */}
                              {booking.isMultiCurrency && booking.presentmentCurrency && (
                                <p className="text-xs text-blue-600 mt-1">
                                  💱 Paid in {booking.presentmentCurrency.toUpperCase()}
                                </p>
                              )}
                              {isFailed && (
                                <p className="text-xs text-gray-400 mt-1">Payment Failed</p>
                              )}
                              {/* Refund Button - Show for paid bookings (modal handles Stripe check) */}
                              {isPaid && booking.paymentStatus !== 'refunded' && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="mt-2 text-orange-600 border-orange-200 hover:bg-orange-50 hover:text-orange-700"
                                  onClick={() => {
                                    setSelectedBookingForRefund(booking.bookingId || booking.id);
                                    setRefundModalOpen(true);
                                  }}
                                >
                                  <RotateCcw className="w-3 h-3 mr-1" />
                                  Refund
                                </Button>
                              )}
                              {booking.paymentStatus === 'refunded' && (
                                <span className="mt-2 inline-block text-xs px-2 py-0.5 rounded-full bg-orange-50 text-orange-700 border border-orange-200">Refunded</span>
                              )}
                              {booking.paymentStatus === 'partially_refunded' && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="mt-2 text-orange-600 border-orange-200 hover:bg-orange-50"
                                  onClick={() => {
                                    setSelectedBookingForRefund(booking.bookingId || booking.id);
                                    setRefundModalOpen(true);
                                  }}
                                >
                                  <RotateCcw className="w-3 h-3 mr-1" />
                                  Partial Refund
                                </Button>
                              )}
                            </div>
                          </div>
                          
                          {/* Co-participants on this booking */}
                          {booking.coParticipants?.length > 0 && (
                            <div className="mt-4 pt-4 border-t border-gray-100">
                              <p className="text-xs font-medium text-gray-500 mb-2">Other participants on this trip:</p>
                              <div className="flex flex-wrap gap-2">
                                {booking.coParticipants.map((cp, cpIdx) => (
                                  <div key={cpIdx} className="flex items-center gap-2 bg-gray-100 rounded-full px-3 py-1 text-xs">
                                    <div className="w-5 h-5 rounded-full bg-gray-300 flex items-center justify-center text-[10px] font-medium">
                                      {(cp.name || 'U')[0].toUpperCase()}
                                    </div>
                                    <span>{cp.name}</span>
                                    {cp.phone && <span className="text-gray-400">({cp.phone})</span>}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <PaginationControls paginated={paginatedBookings} setPage={setBookingsPage} />
                </>
              )}
            </div>
          )}

          {/* Waivers Tab */}
          {activeTab === 'waivers' && (
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900 mb-4">Signed Waivers ({details?.waiverSignatures?.length || 0})</h3>
              {details?.waiverSignatures?.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No waivers signed</p>
              ) : (
                <>
                  <div className="space-y-3">
                    {paginatedWaivers.items?.map((waiver, idx) => {
                      const deviceInfo = waiver.deviceInfo || {};
                      const verificationMethod = waiver.verificationMethod || 'unknown';
                      const verificationLabels = {
                        'email': 'Email PIN Verified',
                        'sms': 'SMS PIN Verified',
                        'id': 'ID Upload',
                        'team_member_id_check': 'Team Member ID Check',
                        'unknown': 'Unknown'
                      };
                      
                      return (
                        <div key={idx} className="border border-purple-200 bg-purple-50/50 rounded-lg p-4">
                          <div className="flex items-start justify-between">
                            <div>
                              <h4 className="font-semibold text-gray-900">Waiver for {waiver.serviceName}</h4>
                              <p className="text-sm text-gray-600 mt-1">Trip date: {formatDate(waiver.bookingDate)}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              {waiver.waiverVersion && (
                                <Badge className="bg-gray-100 text-gray-700">
                                  v{waiver.waiverVersion}
                                </Badge>
                              )}
                              <Badge className={`${
                                verificationMethod === 'email' ? 'bg-blue-100 text-blue-700' :
                                verificationMethod === 'sms' ? 'bg-green-100 text-green-700' :
                                verificationMethod === 'id' ? 'bg-amber-100 text-amber-700' :
                                'bg-gray-100 text-gray-700'
                              }`}>
                                {verificationLabels[verificationMethod] || verificationMethod}
                              </Badge>
                              <Badge className="bg-purple-100 text-purple-700">Signed</Badge>
                            </div>
                          </div>
                          
                          {/* Basic Info */}
                          <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <p className="text-gray-500">Signer Name</p>
                              <p className="font-medium">{waiver.signatureName || 'N/A'}</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Phone Number</p>
                              <p className="font-medium">{waiver.phoneNumber || 'N/A'}</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Email</p>
                              <p className="font-medium text-xs">{waiver.email || waiver.verifiedContact || 'N/A'}</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Signed At</p>
                              <p className="font-medium">{formatDate(waiver.signedAt)}</p>
                            </div>
                          </div>
                          
                          {/* Device Fingerprint - Critical for Evidence & Reports */}
                          <div className="mt-4 pt-4 border-t border-purple-200">
                            <p className="text-xs font-medium text-gray-500 mb-3 flex items-center gap-1">
                              <Monitor className="w-3 h-3" />
                              Device Fingerprint (Evidence Record)
                            </p>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
                              <div className="bg-white rounded p-2 border border-gray-200">
                                <p className="text-gray-400">IP Address</p>
                                <p className="font-mono font-medium">{waiver.ipAddress || 'N/A'}</p>
                              </div>
                              <div className="bg-white rounded p-2 border border-gray-200">
                                <p className="text-gray-400">Platform</p>
                                <p className="font-medium">{deviceInfo.platform || 'N/A'}</p>
                              </div>
                              <div className="bg-white rounded p-2 border border-gray-200">
                                <p className="text-gray-400">Browser</p>
                                <p className="font-medium">{deviceInfo.browser || 'N/A'}</p>
                              </div>
                              <div className="bg-white rounded p-2 border border-gray-200">
                                <p className="text-gray-400">Screen Size</p>
                                <p className="font-medium">{deviceInfo.screenResolution || deviceInfo.screenSize || 'N/A'}</p>
                              </div>
                              <div className="bg-white rounded p-2 border border-gray-200">
                                <p className="text-gray-400">Language</p>
                                <p className="font-medium">{deviceInfo.language || 'N/A'}</p>
                              </div>
                              <div className="bg-white rounded p-2 border border-gray-200">
                                <p className="text-gray-400">Timezone</p>
                                <p className="font-medium text-[10px]">{deviceInfo.timezone || 'N/A'}</p>
                              </div>
                            </div>
                            {deviceInfo.userAgent && (
                              <div className="mt-2 bg-white rounded p-2 border border-gray-200">
                                <p className="text-gray-400 text-xs">User Agent</p>
                                <p className="font-mono text-[10px] break-all">{deviceInfo.userAgent}</p>
                              </div>
                            )}
                          </div>
                          
                          {/* Signature Image */}
                          {(() => {
                            // Get signature - prefer signatureData (new compressed), fallback to others
                            const getSignatureSource = () => {
                              // New format: signatureData is compressed JPEG base64
                              if (waiver.signatureData && waiver.signatureData.startsWith('data:')) {
                                return waiver.signatureData;
                              }
                              // Legacy: signatureUrl/signatureImage
                              const sigUrl = waiver.signatureUrl || waiver.signatureImage;
                              if (!sigUrl) return null;
                              if (sigUrl.startsWith('data:') || sigUrl.startsWith('http')) return sigUrl;
                              const apiBase = process.env.REACT_APP_BACKEND_URL || '';
                              return `${apiBase}${sigUrl}`;
                            };
                            const signatureSrc = getSignatureSource();
                            
                            return signatureSrc ? (
                              <div className="mt-4 pt-4 border-t border-purple-200">
                                <p className="text-xs font-medium text-gray-500 mb-2">Digital Signature:</p>
                                <div className="bg-white rounded border border-gray-200 p-2 inline-block">
                                  <img 
                                    src={signatureSrc} 
                                    alt="Signature" 
                                    className="h-16 max-w-[200px] object-contain"
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                  />
                                </div>
                              </div>
                            ) : null;
                          })()}
                          
                          {/* Emergency Contact */}
                          {waiver.emergencyContact && (
                            <div className="mt-4 pt-4 border-t border-purple-200">
                              <p className="text-xs font-medium text-gray-500 mb-2">Emergency Contact:</p>
                              <p className="text-sm">{waiver.emergencyContact} - {waiver.emergencyPhone}</p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <PaginationControls paginated={paginatedWaivers} setPage={setWaiversPage} />
                </>
              )}
            </div>
          )}

          {/* Connections Tab */}
          {activeTab === 'connections' && (
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900 mb-4">Connected People ({details?.coParticipants?.length || 0})</h3>
              <p className="text-sm text-gray-500 mb-4">
                People who have been on trips with this contact (waiver co-signers, group members, etc.)
              </p>
              {details?.coParticipants?.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No connections found</p>
              ) : (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {paginatedConnections.items?.map((person, idx) => (
                      <div key={idx} className="border border-gray-200 rounded-lg p-4 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-sm font-medium text-gray-600">
                          {(person.name || 'U')[0].toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 truncate">{person.name}</p>
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            {person.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{person.phone}</span>}
                            {person.email && <span className="flex items-center gap-1 truncate"><Mail className="w-3 h-3" />{person.email}</span>}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <PaginationControls paginated={paginatedConnections} setPage={setConnectionsPage} />
                </>
              )}
            </div>
          )}
        </div>

        {/* Refund Modal */}
        <RefundModal
          isOpen={refundModalOpen}
          onClose={() => {
            setRefundModalOpen(false);
            setSelectedBookingForRefund(null);
          }}
          bookingId={selectedBookingForRefund}
          onRefundComplete={() => {
            // Refresh the contact details to show updated payment status
            const fetchDetails = async () => {
              try {
                const response = await api.get(`/relations/${encodeURIComponent(contact.id)}/details`);
                setDetails(response.data);
              } catch (error) {
                console.error('Error refreshing details:', error);
              }
            };
            fetchDetails();
          }}
        />

        {/* Print-only: All data */}
        <div className="hidden print:block p-6 space-y-8">
          <h3 className="font-bold text-lg border-b pb-2">Complete Contact Record</h3>
          <p className="text-xs text-gray-500">Generated: {new Date().toLocaleString()}</p>
          
          {/* All bookings for print */}
          <div>
            <h4 className="font-semibold mb-2">All Bookings ({details?.bookings?.length || 0})</h4>
            {details?.bookings?.map((b, i) => (
              <div key={i} className="border-b py-2 text-sm">
                <p><strong>{b.serviceName}</strong> - {formatDate(b.date)}</p>
                <p>Amount: ${b.amount?.toFixed(2)} | Status: {b.status} | Payment: {b.paymentStatus}</p>
                {b.stripePaymentIntentId && <p className="text-xs">Payment ID: {b.stripePaymentIntentId}</p>}
              </div>
            ))}
          </div>
          
          {/* All waivers for print */}
          <div>
            <h4 className="font-semibold mb-2">All Waivers ({details?.waiverSignatures?.length || 0})</h4>
            {details?.waiverSignatures?.map((w, i) => (
              <div key={i} className="border-b py-2 text-sm">
                <p><strong>{w.serviceName}</strong></p>
                <p>Signer: {w.signatureName} | Phone: {w.phoneNumber}</p>
                <p>Signed: {formatDate(w.signedAt)} | IP: {w.ipAddress}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  // Use portal to render outside of React component hierarchy  
  return ReactDOM.createPortal(content, document.body);
};

const RelationsTab = ({ initialFilter = 'all' }) => {
  // Filters - use initialFilter prop
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [statusFilter, setStatusFilter] = useState(initialFilter);
  const [sortBy, setSortBy] = useState('recent');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(1);
  const limit = 20;
  
  // Export
  const [exporting, setExporting] = useState(false);
  
  // Detail panel
  const [selectedContact, setSelectedContact] = useState(null);
  
  // Copy to clipboard animation state
  const [copiedItems, setCopiedItems] = useState({});
  
  // Status dropdown state
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(null);

  // Build SWR key for data fetching
  const swrKey = useMemo(() => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      search: searchQuery,
      status: statusFilter,
      sort_by: sortBy,
      sort_dir: sortDir
    });
    return `/relations?${params}`;
  }, [page, searchQuery, statusFilter, sortBy, sortDir]);

  // Fetch data with useSWR - auto-revalidates on focus
  const { data, error, isLoading, isValidating, mutate } = useSWR(
    swrKey,
    async (url) => {
      const response = await api.get(url);
      return response.data;
    },
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
      dedupingInterval: 2000,
      keepPreviousData: true,
    }
  );

  // Extract data from SWR response
  const contacts = data?.data || [];
  const stats = data?.stats || { total: 0, customers: 0, leads: 0, waivers: 0, returning: 0, ghost: 0 };
  const pagination = data?.pagination || { page: 1, limit: 20, total: 0, totalPages: 0 };
  const loading = isLoading;
  const isInitialLoading = isLoading && !data;

  // Currency formatting helper (also used in list)
  const formatCurrency = (amount, currency = 'USD') => {
    const currencySymbols = {
      'USD': '$',
      'AED': 'AED ',
      'EUR': '€',
      'GBP': '£',
      'INR': '₹',
    };
    const symbol = currencySymbols[currency?.toUpperCase()] || currency + ' ';
    return `${symbol}${(amount || 0).toFixed(2)}`;
  };
  
  // Copy to clipboard with animation
  const handleCopy = (text, key) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied!');
    setCopiedItems(prev => ({ ...prev, [key]: true }));
    setTimeout(() => {
      setCopiedItems(prev => ({ ...prev, [key]: false }));
    }, 3000);
  };
  
  // Update contact status
  const handleStatusChange = async (contactId, newStatus) => {
    try {
      const token = localStorage.getItem('jwtToken');
      const response = await fetch(`${BACKEND_URL}/api/relations/${encodeURIComponent(contactId)}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });
      
      if (!response.ok) throw new Error('Failed to update status');
      
      // Revalidate data via SWR
      mutate();
      toast.success(`Status updated to ${newStatus}`);
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    } finally {
      setStatusDropdownOpen(null);
    }
  };

  // Close status dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      if (statusDropdownOpen) setStatusDropdownOpen(null);
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [statusDropdownOpen]);

  // Debounce search (300ms) with sanitization
  useEffect(() => {
    const timer = setTimeout(() => {
      const sanitized = sanitizeSearchInput(searchInput);
      setSearchQuery(sanitized);
      setPage(1); // Reset to page 1 on search
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Lead actions - now use mutate() to refresh data
  const markLeadAsViewed = async (contact, e) => {
    e.stopPropagation();
    if (!contact.leadId) return;
    try {
      await api.patch(`/leads/${contact.leadId}`, { 
        viewedBy: 'admin', 
        viewedAt: new Date().toISOString() 
      });
      mutate();
      toast.success('Marked as viewed');
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const markLeadAsContacted = async (contact, e) => {
    e.stopPropagation();
    if (!contact.leadId) return;
    try {
      await api.patch(`/leads/${contact.leadId}`, { 
        status: 'contacted', 
        contactedAt: new Date().toISOString() 
      });
      mutate();
      toast.success('Marked as contacted');
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const convertLead = async (contact, e) => {
    e.stopPropagation();
    if (!contact.leadId) return;
    if (!confirm('Mark as converted?')) return;
    try {
      await api.patch(`/leads/${contact.leadId}`, { 
        status: 'converted', 
        convertedAt: new Date().toISOString() 
      });
      mutate();
      toast.success('Lead converted!');
    } catch (error) {
      toast.error('Failed to convert');
    }
  };

  // Export - accepts format from ExportDropdown
  const handleExport = async (format = 'csv') => {
    try {
      setExporting(true);
      const response = await api.get(`/relations/export?status=${statusFilter}&format=${format}`, {
        responseType: 'blob'
      });
      
      const contentType = format === 'json' ? 'application/json' : 'text/csv';
      const extension = format === 'json' ? 'json' : 'csv';
      
      const blob = new Blob([response.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `relations-${new Date().toISOString().split('T')[0]}.${extension}`;
      a.click();
      window.URL.revokeObjectURL(url);
      
      toast.success('Export downloaded!');
    } catch (error) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  // Get status badge config - uses unified color palette
  const getStatusBadge = (contact) => {
    return getStatusBadgeFromPalette(contact);
  };
  
  // Check if contact is a returning customer (has made previous purchases and has recent lead activity)
  const isReturningLead = (contact) => {
    return contact.isReturningCustomer && (contact.status === 'lead' || contact.status === 'recent_lead');
  };

  return (
    <TabPageLayout
      title="Relations"
      subtitle="Click any contact to view full history, waivers, and connected people"
      loading={isInitialLoading}
      actions={
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => mutate()}
            disabled={isValidating}
            className="h-9"
          >
            <RefreshCw className={`w-4 h-4 ${isValidating ? 'animate-spin' : ''}`} />
          </Button>
          <ExportDropdown 
            onExport={handleExport}
            disabled={exporting || stats.total === 0}
            loading={exporting}
            formats={['csv']}
          />
        </div>
      }
    >
      <div className="space-y-6">
        {/* Status Filter Pills - Simplified: All, Customers, Leads, Returning, Ghost, Waivers */}
        <div className="flex flex-wrap gap-2">
        {[
          { key: 'all', label: 'All', count: stats.total },
          { key: 'customer', label: 'Customers', count: stats.customers },
          { key: 'lead', label: 'Leads', count: stats.leads },
          { key: 'returning', label: 'Returning', count: stats.returning || 0 },
          { key: 'ghost', label: 'Ghost', count: stats.ghost || 0 },
          { key: 'waiver', label: 'Waivers', count: stats.waivers }
        ].map(({ key, label, count }) => (
          <button
            key={key}
            onClick={() => { setStatusFilter(key); setPage(1); }}
            className={`inline-flex items-center gap-2 px-3 py-1.5 h-9 rounded-lg text-sm font-medium transition-colors border ${
              statusFilter === key 
                ? 'bg-blue-600 text-white border-blue-600' 
                : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
            }`}
          >
            {label}
            <span className={`text-xs px-1.5 py-0.5 rounded ${
              statusFilter === key ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-600'
            }`}>
              {count}
            </span>
          </button>
        ))}
      </div>

      {/* Search & Sort Bar */}
      <div className="bg-white rounded-xl p-4 shadow-[0_8px_24px_rgba(0,0,0,0.04)]">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <label htmlFor="contact-search" className="sr-only">Search contacts</label>
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              id="contact-search"
              type="text"
              placeholder="Search by name, email, or phone..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-10 h-10 bg-gray-50 border-gray-200 focus:bg-white"
            />
            {searchInput && (
              <button
                onClick={() => setSearchInput('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                aria-label="Clear search"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500 hidden sm:inline">Sort:</span>
            {[
              { key: 'recent', label: 'Recent' },
              { key: 'name', label: 'Name' },
              { key: 'bookings', label: 'Bookings' },
              { key: 'revenue', label: 'Revenue' }
            ].map(sort => (
              <button
                key={sort.key}
                onClick={() => {
                  if (sortBy === sort.key) {
                    setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
                  } else {
                    setSortBy(sort.key);
                    setSortDir(sort.key === 'name' ? 'asc' : 'desc');
                  }
                  setPage(1);
                }}
                className={`px-3 py-1.5 h-9 text-xs rounded-lg font-medium transition-colors border ${
                  sortBy === sort.key
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                }`}
              >
                {sort.label}
                {sortBy === sort.key && (
                  <span className="ml-1">{sortDir === 'asc' ? '↑' : '↓'}</span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Contacts Table */}
      <div className="bg-white rounded-xl overflow-hidden shadow-[0_8px_24px_rgba(0,0,0,0.04)]">
        {isValidating && !isInitialLoading ? (
          <div className="flex items-center justify-center py-20">
            <PageLoader minHeight="h-20" />
          </div>
        ) : contacts.length === 0 ? (
          <div className="text-center py-20">
            <User className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-1">
              {searchQuery || statusFilter !== 'all' ? 'No contacts found' : 'No contacts yet'}
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              {searchQuery || statusFilter !== 'all' 
                ? 'Try adjusting your search or filters' 
                : 'Contacts will appear after bookings, leads, or waivers'}
            </p>
            {(searchQuery || statusFilter !== 'all') && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setSearchInput('');
                  setStatusFilter('all');
                  setPage(1);
                }}
                className="h-9"
              >
                Clear Filters
              </Button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-3">Name</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">Status</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3 hidden md:table-cell">Email</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3 hidden lg:table-cell">Phone</th>
                  <th className="text-center text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">Bookings</th>
                  <th className="text-center text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">Waivers</th>
                  <th className="text-right text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">Revenue</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3 hidden xl:table-cell">Joined</th>
                  <th className="text-right text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {contacts.map((contact, idx) => {
                  const statusBadge = getStatusBadge(contact);
                  const isNewLead = contact.status === 'lead' && !contact.leadViewedBy;
                  
                  // Get first activity date (joined date)
                  const joinedDate = contact.firstSeen || contact.createdAt || contact.leadCreatedAt;
                  const formattedJoined = joinedDate ? new Date(joinedDate).toLocaleDateString('en-US', { 
                    month: 'short', 
                    day: 'numeric',
                    year: 'numeric'
                  }) : '—';
                  
                  return (
                    <tr 
                      key={contact.id || idx} 
                      onClick={() => setSelectedContact(contact)}
                      className="hover:bg-gray-50 transition-colors cursor-pointer"
                    >
                      {/* Name */}
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${getAvatarBgColor(contact.status)}`}>
                            <span className={`text-sm font-medium ${getAvatarTextColor(contact.status)}`}>
                              {(contact.name || 'U')[0].toUpperCase()}
                            </span>
                          </div>
                          <span className="font-medium text-gray-900 truncate max-w-[150px]">{contact.name || 'Unknown'}</span>
                        </div>
                      </td>
                      
                      {/* Status - Dropdown selector */}
                      <td className="px-4 py-4">
                        <div className="relative">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setStatusDropdownOpen(statusDropdownOpen === contact.id ? null : contact.id);
                            }}
                            className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium border whitespace-nowrap transition-colors ${statusBadge.className} hover:opacity-80`}
                          >
                            {statusBadge.label}
                            <ChevronDown className="w-3 h-3" />
                          </button>
                          
                          {/* Dropdown menu */}
                          {statusDropdownOpen === contact.id && (
                            <div className="absolute z-50 mt-1 left-0 bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[120px]">
                              {[
                                { key: 'lead', label: 'Lead' },
                                { key: 'customer', label: 'Customer' },
                                { key: 'returning', label: 'Returning' },
                                { key: 'ghost', label: 'Ghost' },
                                { key: 'waiver', label: 'Waiver' }
                              ].map(option => {
                                const optionBadge = getStatusBadge({ status: option.key });
                                return (
                                  <button
                                    key={option.key}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleStatusChange(contact.id, option.key);
                                    }}
                                    className={`w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 flex items-center gap-2 ${
                                      contact.status === option.key ? 'bg-gray-50' : ''
                                    }`}
                                  >
                                    <span className={`w-2 h-2 rounded-full ${
                                      option.key === 'lead' ? 'bg-orange-500' :
                                      option.key === 'customer' ? 'bg-emerald-500' :
                                      option.key === 'returning' ? 'bg-orange-500' :
                                      option.key === 'ghost' ? 'bg-gray-400' :
                                      'bg-purple-500'
                                    }`}></span>
                                    {option.label}
                                    {contact.status === option.key && <Check className="w-3 h-3 ml-auto text-blue-600" />}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </td>
                      
                      {/* Email - hidden on mobile */}
                      <td className="px-4 py-4 hidden md:table-cell">
                        {contact.email ? (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCopy(contact.email, `email-${contact.id}`);
                            }}
                            className="group flex items-center gap-1.5 text-sm text-gray-600 hover:text-blue-600"
                            title="Click to copy"
                          >
                            <span className="truncate max-w-[160px]">{contact.email}</span>
                            {copiedItems[`email-${contact.id}`] ? (
                              <Check className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                            ) : (
                              <Copy className="w-3.5 h-3.5 text-gray-400 group-hover:text-blue-600 transition-colors flex-shrink-0" />
                            )}
                          </button>
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </td>
                      
                      {/* Phone - hidden on mobile/tablet */}
                      <td className="px-4 py-4 hidden lg:table-cell">
                        {contact.phone ? (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCopy(contact.phone, `phone-${contact.id}`);
                            }}
                            className="group flex items-center gap-1.5 text-sm text-gray-600 hover:text-blue-600"
                            title="Click to copy"
                          >
                            <span>{contact.phone}</span>
                            {copiedItems[`phone-${contact.id}`] ? (
                              <Check className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                            ) : (
                              <Copy className="w-3.5 h-3.5 text-gray-400 group-hover:text-blue-600 transition-colors flex-shrink-0" />
                            )}
                          </button>
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </td>
                      
                      {/* Bookings */}
                      <td className="px-4 py-4 text-center">
                        <span className={`text-sm font-medium ${contact.bookingCount > 0 ? 'text-gray-900' : 'text-gray-400'}`}>
                          {contact.bookingCount}
                        </span>
                      </td>
                      
                      {/* Waivers */}
                      <td className="px-4 py-4 text-center">
                        <span className={`text-sm font-medium ${contact.waiverCount > 0 ? 'text-gray-900' : 'text-gray-400'}`}>
                          {contact.waiverCount}
                        </span>
                      </td>
                      
                      {/* Revenue */}
                      <td className="px-4 py-4 text-right">
                        <span className={`text-sm font-medium ${contact.totalSpent > 0 ? 'text-gray-900' : 'text-gray-400'}`}>
                          {formatCurrency(contact.totalSpent, contact.currency)}
                        </span>
                      </td>
                      
                      {/* Joined - hidden on smaller screens */}
                      <td className="px-4 py-4 hidden xl:table-cell whitespace-nowrap">
                        <span className="text-sm text-gray-500">
                          {formattedJoined}
                        </span>
                      </td>
                      
                      {/* Actions - Simple View button */}
                      <td className="px-6 py-4 text-right">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedContact(contact);
                          }}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-colors"
                        >
                          View
                          <ChevronRight className="w-3 h-3" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        
        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
            <p className="text-sm text-gray-600">
              Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} contacts
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(p => p - 1)}
                disabled={pagination.page === 1}
                className="p-2 h-9 rounded-lg border border-gray-200 hover:bg-blue-50 hover:border-blue-200 hover:text-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                  let pageNum;
                  if (pagination.totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (pagination.page <= 3) {
                    pageNum = i + 1;
                  } else if (pagination.page >= pagination.totalPages - 2) {
                    pageNum = pagination.totalPages - 4 + i;
                  } else {
                    pageNum = pagination.page - 2 + i;
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setPage(pageNum)}
                      className={`w-8 h-9 rounded-lg text-sm font-medium transition-colors ${
                        pagination.page === pageNum
                          ? 'bg-blue-600 text-white'
                          : 'hover:bg-gray-100 text-gray-600'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              
              <button
                onClick={() => setPage(p => p + 1)}
                disabled={pagination.page === pagination.totalPages}
                className="p-2 h-9 rounded-lg border border-gray-200 hover:bg-blue-50 hover:border-blue-200 hover:text-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
      </div>

      {/* Detail Panel Modal */}
      {selectedContact && (
        <ContactDetailPanel 
          contact={selectedContact} 
          onClose={() => setSelectedContact(null)} 
        />
      )}
    </TabPageLayout>
  );
};

export default RelationsTab;
